import { useRef, useState } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, X, ImageSquare } from "@phosphor-icons/react";

/** Resolves a stored value (absolute URL, relative path, or /api/uploads/...) to a previewable URL. */
function resolveSrc(value) {
  if (!value) return "";
  if (value.startsWith("/api/uploads/")) return `${process.env.REACT_APP_BACKEND_URL}${value}`;
  return value;
}

export default function ImageField({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Le fichier doit être une image."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image trop volumineuse (max 5 Mo)."); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = localStorage.getItem("cdd_token");
      const res = await fetch(`${API_BASE}/uploads`, {
        method: "POST",
        body: form,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Échec de l'upload");
      }
      const data = await res.json();
      onChange(data.url);
      toast.success("Image envoyée");
    } catch (err) {
      toast.error(err.message || "Erreur d'upload");
    } finally {
      setBusy(false);
    }
  };

  const src = resolveSrc(value);

  return (
    <div className="flex items-start gap-4">
      <div className="w-32 h-24 shrink-0 bg-ducs-night border border-ducs-gold/20 rounded-sm flex items-center justify-center overflow-hidden">
        {src
          ? <img src={src} alt="" className="w-full h-full object-cover" />
          : <ImageSquare size={28} className="text-ducs-mist/50" />}
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL de l'image ou /api/uploads/…"
          className="w-full bg-ducs-night border border-ducs-gold/25 px-3 py-2 text-ducs-cream rounded-sm text-sm focus:border-ducs-gold"
        />
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 border border-ducs-gold/40 text-ducs-gold hover:bg-ducs-gold/10 rounded-sm disabled:opacity-60"
          >
            <UploadSimple size={14} /> {busy ? "Envoi…" : "Téléverser une image"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 text-ducs-mist hover:text-red-400"
            >
              <X size={14} /> Effacer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
